/** SVG basketball court that plots REAL shots at their feed coordinates. */
import { memo, useEffect, useId, useMemo, useState } from 'react';
import type { GameEvent, Side } from '@clutch/core';
import { COURT, attackingEnd, feedToCourt, hasCoords, type Pt } from './court';

export type CourtProps = {
  plays: readonly GameEvent[];
  period: number;
  /** Only plays at or before this seq are drawn (rewind scrubs the court back). */
  uptoSeq: number;
  /** A play that arrived live: its mark gets the flight + burst animation. */
  liveSeq?: number;
  /** A play to ring in volt (rewind beat, buzzer). */
  highlightSeq?: number;
  /** Only this captioned action may show a ball or flight. Older shots stay as history dots. */
  activeSeq?: number;
  vertical?: boolean;
  maxMarks?: number;
};

const W = COURT.width;
const L = COURT.length;
const RIM = COURT.rimFromBaseline;

/** Court feet → SVG user units for the chosen orientation (vertical = rotated 90° clockwise). */
export function project(p: Pt, vertical: boolean): Pt {
  return vertical ? { x: W - p.y, y: p.x } : p;
}

function End() {
  // One end of the floor, drawn at the LEFT basket; mirrored for the right.
  const c = W / 2;
  const laneTop = c - COURT.laneWidth / 2;
  const t = c - Math.sqrt(COURT.threeRadius ** 2 - (COURT.cornerLength - RIM) ** 2);
  const b = W - t;
  return (
    <g className="court__end">
      <rect className="court__paint" x={0} y={laneTop} width={COURT.laneLength} height={COURT.laneWidth} />
      <path className="court__line" d={`M0 ${laneTop} H${COURT.laneLength} V${laneTop + COURT.laneWidth} H0`} />
      <path
        className="court__line"
        d={`M${COURT.laneLength} ${c - COURT.ftCircleRadius} A${COURT.ftCircleRadius} ${COURT.ftCircleRadius} 0 0 1 ${COURT.laneLength} ${c + COURT.ftCircleRadius}`}
      />
      <path
        className="court__line court__line--dash"
        d={`M${COURT.laneLength} ${c - COURT.ftCircleRadius} A${COURT.ftCircleRadius} ${COURT.ftCircleRadius} 0 0 0 ${COURT.laneLength} ${c + COURT.ftCircleRadius}`}
      />
      <path
        className="court__line"
        d={`M${RIM} ${c - COURT.restrictedRadius} A${COURT.restrictedRadius} ${COURT.restrictedRadius} 0 0 1 ${RIM} ${c + COURT.restrictedRadius}`}
      />
      <path
        className="court__line court__line--three"
        d={`M0 ${t} H${COURT.cornerLength} A${COURT.threeRadius} ${COURT.threeRadius} 0 0 1 ${COURT.cornerLength} ${b} H0`}
      />
      <line className="court__board" x1={COURT.backboardFromBaseline} x2={COURT.backboardFromBaseline} y1={c - 3} y2={c + 3} />
      <circle className="court__rim" cx={RIM} cy={c} r={COURT.rimRadius} />
    </g>
  );
}

const Lines = memo(function Lines({ vertical, leftSide, rightSide }: { vertical: boolean; leftSide?: Side; rightSide?: Side }) {
  const grain = useId();
  return (
    <g transform={vertical ? `matrix(0 1 -1 0 ${W} 0)` : undefined}>
      <defs><pattern id={grain} width="7.8" height="50" patternUnits="userSpaceOnUse"><rect width="3.9" height="50" fill="#fff" opacity=".025" /><path d="M0 0V50M3.9 0V50" stroke="#fff" strokeOpacity=".035" strokeWidth=".1" /></pattern></defs>
      <rect className="court__floor" x={0} y={0} width={L} height={W} />
      <rect x={0} y={0} width={L} height={W} fill={`url(#${grain})`} />
      <rect className={`court__glow court__glow--${leftSide ?? 'none'}`} x={0} y={0} width={L / 2} height={W} />
      <rect className={`court__glow court__glow--r court__glow--${rightSide ?? 'none'}`} x={L / 2} y={0} width={L / 2} height={W} />
      <End />
      <g transform={`translate(${L} 0) scale(-1 1)`}>
        <End />
      </g>
      <line className="court__line" x1={L / 2} x2={L / 2} y1={0} y2={W} />
      <circle className="court__line" cx={L / 2} cy={W / 2} r={COURT.centerCircleRadius} />
      <circle className="court__line" cx={L / 2} cy={W / 2} r={2} />
      <text className="court__brand" x={L / 2} y={W / 2 + 1.1} textAnchor="middle">CLUTCH</text>
      <rect className="court__line court__bounds" x={0} y={0} width={L} height={W} />
    </g>
  );
});

export function Court({ plays, period, uptoSeq, liveSeq, highlightSeq, activeSeq, vertical = false, maxMarks = 6 }: CourtProps) {
  const [reduceMotion, setReduceMotion] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => setReduceMotion(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  const shots = useMemo(
    () => plays.filter((e) => e.type === 'shot' && e.seq <= uptoSeq && hasCoords(e)).slice(-maxMarks),
    [plays, uptoSeq, maxMarks],
  );
  const highlight = highlightSeq !== undefined ? plays.find((e) => e.seq === highlightSeq && hasCoords(e)) : undefined;
  const shown = highlight && !shots.includes(highlight) ? [...shots, highlight] : shots;
  const latest = plays.filter((e) => e.type === 'shot' && e.seq <= uptoSeq).at(-1);

  // Tint each basket with the team attacking it this period (from this period's real shots).
  const upto = plays.filter((e) => e.seq <= uptoSeq);
  const curPeriod = upto[upto.length - 1]?.period ?? period;
  const homeEnd = attackingEnd(upto, 'home', curPeriod);
  const awayEnd = attackingEnd(upto, 'away', curPeriod);
  const leftSide: Side | undefined = homeEnd === 'left' ? 'home' : awayEnd === 'left' ? 'away' : undefined;
  const rightSide: Side | undefined = homeEnd === 'right' ? 'home' : awayEnd === 'right' ? 'away' : undefined;

  const vb = vertical ? `-1 -1 ${W + 2} ${L + 2}` : `-1 -1 ${L + 2} ${W + 2}`;
  return (
    <svg className="court" viewBox={vb} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Court: real shot locations">
      <Lines vertical={vertical} leftSide={leftSide} rightSide={rightSide} />
      <g className="court__marks">
        {shown.map((e, i) => {
          const p = project(feedToCourt(e.x!, e.y!), vertical);
          const age = shots.length > 1 ? i / (shots.length - 1) : 1;
          const isLatest = e === latest && (activeSeq === undefined || e.seq === activeSeq);
          const isLive = e.seq === liveSeq;
          const isHi = e === highlight;
          const side = e.side ?? 'home';
          const rim = project({ x: e.x! < 50 ? RIM : L - RIM, y: W / 2 }, vertical);
          return (
            <g
              key={e.seq}
              className={`mark mark--${side}${e.made ? ' is-made' : ' is-miss'}${isLatest ? ' is-latest' : ''}${isLive ? ' is-live' : ''}${isHi ? ' is-hi' : ''}`}
              style={{ opacity: isLatest || isHi ? 1 : 0.15 + 0.25 * age }}
              data-seq={e.seq}
            >
              {e.made ? (
                <circle className="mark__dot" cx={p.x} cy={p.y} r={isLatest || isHi ? 1.55 : 1.05} />
              ) : (
                <circle className="mark__ring" cx={p.x} cy={p.y} r={isLatest || isHi ? 1.35 : 0.95} />
              )}
              {isLive && e.made && <circle className="mark__burst" cx={p.x} cy={p.y} r={1.6} />}
              {isHi && <circle className="mark__hi" cx={p.x} cy={p.y} r={3} />}
              {(isLatest || isHi) && e.made && e.points ? (
                <text className="mark__pts" x={p.x} y={p.y - 2.6} textAnchor="middle">
                  +{e.points}
                </text>
              ) : null}
              {(isLatest || isHi) && <Flight from={p} to={rim} live={isLive && !reduceMotion} made={!!e.made} />}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/** Illustrative arc from the feed's shot location to the rim, not optical ball tracking. */
function Flight({ from, to, live, made }: { from: Pt; to: Pt; live: boolean; made: boolean }) {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const lift = Math.min(9, len * 0.35);
  // Bow the arc "up" the screen (toward smaller y) for a broadcast-style loft.
  const nx = -dy / len;
  const ny = dx / len;
  const s = ny > 0 ? -1 : 1;
  const c = { x: mx + nx * lift * s, y: my + ny * lift * s };
  const path = `M${from.x} ${from.y} Q${c.x} ${c.y} ${to.x} ${to.y}`;
  return <>
    <path className={`mark__flight${live ? ' is-live' : ''}`} d={path} pathLength={1} />
    {live && made && <circle className="court__swish" cx={to.x} cy={to.y} r="2" />}
    <g className="court__ball" data-testid="court-ball" transform={live ? undefined : `translate(${from.x} ${from.y})`}>
      {live && <animateMotion path={path} dur="1.3s" fill="freeze" calcMode="spline" keyTimes="0;1" keySplines=".2 .5 .3 1" />}
      <circle className="court__ball-shadow" cy=".35" r="2.15" />
      <circle className="court__ball-body" r="1.85" />
      <path className="court__ball-seams" d="M-1.83 0H1.83M0-1.83V1.83M-1.3-1.3Q.5 0-1.3 1.3M1.3-1.3Q-.5 0 1.3 1.3" />
      <circle className="court__ball-shine" cx="-.55" cy="-.65" r=".45" />
    </g>
  </>;
}
